import React from 'react';
import {
  ActionIcon,
  Badge, Card, DetailStack, Dropdown, FontWeight, HStack, ICON_EYE_OUTLINE, Message, Stack, Text, UserPreview, UserPreviewSkeleton,
} from '@doveit/bricks';
import { formatEuro } from '@doveit/hammer';
import { Invoice } from '../../../providers/api/dtos';
import useContact from '../../../contact/hooks/use-contact/useContact';
import useProperty from '../../../property/hooks/use-property/useProperty';
import ViewPropertyPopoverPreview from '../../../property/containers/view-property-popover-preview/ViewPropertyPopoverPreview';
import PropertyAvatar from '../../../property/components/property-avatar/PropertyAvatar';
import { setVerticalEllipsis } from '../../../utils/styles/verticalEllipsis.style';
import ViewContactPopoverPreview from '../../../contact/containers/view-contact-popover-preview/ViewContactPopoverPreview';
import { NO_VALUE_SYMBOL } from '../../../property/utils';
import { invoiceTypeLabels } from '../../../labels/invoiceTypeLabels';
import { InvoiceStatus, ReferenceType } from '../../../domain/types';
import DateViewer from '../../../components/date-viewer/DateViewer';
import Divider from '../../../components/divider/Divider';
import useAssignmentById from '../../../assignment/hooks/use-assignment-by-id/useAssignmentById';
import { getCriticalIfInThePast, getPaymentDateLabel } from '../utils';
import { goToDetailPage } from '../../../utils/navigate/utils';
import UpsertInvoiceAction from '../upsert-invoice-action/UpsertInvoiceAction';
import UpdateInvoiceStatus from '../update-invoice-status/UpdateInvoiceStatus';
import useCompany from '../../../company/hooks/use-company/useCompany';
import ViewCompanyPopoverPreview from '../../../company/containers/view-company-popover-preview/ViewCompanyPopoverPreview';
import CompanyAvatar from '../../../company/components/company-avatar/CompanyAvatar';
import InvoiceValuePopover from '../../components/invoice-value-popover/InvoiceValuePopover';
import { computeGrossValue } from '../../utils';
import ManageInvoiceCommissionsAction from '../manage-invoice-commissions-action/ManageInvoiceCommissionsAction';
import DeleteInvoiceAction from '../delete-invoice-action/DeleteInvoiceAction';
import useRBAC from '../../../hooks/use-rbac/useRBAC';

export interface ViewInvoiceCardProps {
  invoice: Invoice,
  context: 'HOLDER' | 'PROPERTY',
  canView?: boolean,
  canEdit?: boolean,
  onEdit?: (invoice: Invoice) => void,
}

const ViewInvoiceCard: React.FC<ViewInvoiceCardProps> = ({
  invoice,
  context,
  canView = false,
  canEdit = false,
  onEdit,
}) => {
  const { data: assignment, isLoading: isAssignmentLoading, error: assignmentError } = useAssignmentById(invoice.assignmentId);
  const { data: property, isLoading: isPropertyLoading, error: propertyError } = useProperty(assignment?.propertyId);
  const { data: contact, isLoading: isContactLoading, error: contactError } = useContact(invoice.reference.type === ReferenceType.CONTACT ? invoice.reference.id : undefined);
  const { data: company, isLoading: isCompanyLoading, error: companyError } = useCompany(invoice.reference.type === ReferenceType.COMPANY ? invoice.reference.id : undefined);
  const { userIsAdmin, userIsFinance } = useRBAC();

  const goToInvoiceDetailPage = React.useCallback(() => {
    goToDetailPage(invoice.id!, ReferenceType.INVOICE);
  }, [invoice.id]);

  const disabledInvoiceFields = React.useMemo(() => ({
    property: context === 'PROPERTY',
    contact: context === 'HOLDER',
    company: context === 'HOLDER',
  }), [context]);

  const paymentDateLabel = React.useMemo(() => getPaymentDateLabel(invoice.status), [invoice.status]);
  const grossValue = React.useMemo(() => computeGrossValue(invoice.value, invoice.vat), [invoice.value, invoice.vat]);
  const amountLabel = grossValue !== undefined ? 'Importo' : 'Importo (netto)';
  const displayAmount = grossValue ?? invoice.value;

  return (
    <Card>
      <Card.Header
        primaryActions={[canView && (
          <ActionIcon
            label="Visualizza"
            aria-label="Visualizza dettagli pagamento"
            icon={{ path: ICON_EYE_OUTLINE }}
            size="S"
            onClick={goToInvoiceDetailPage}
          />
        )]}
        secondaryActions={canEdit ? [
          invoice.status !== InvoiceStatus.NOT_PAID && (
            <UpsertInvoiceAction
              invoice={invoice}
              onSuccess={onEdit}
              disabledFields={disabledInvoiceFields}
            >
              {({ openUpsertInvoiceModal }) => (
                <Dropdown.Option
                  label="Modifica"
                  aria-label="Modifica pagamento"
                  onClick={openUpsertInvoiceModal}
                />
              )}
            </UpsertInvoiceAction>
          ),
          [InvoiceStatus.DRAFT, InvoiceStatus.PROFORMA].includes(invoice.status) && (
            <DeleteInvoiceAction
              key="delete"
              invoiceId={invoice.id!}
              onSuccess={() => onEdit?.(invoice)}
            >
              {({ onOpenModal }) => (
                <Dropdown.Option
                  label="Elimina"
                  aria-label="Elimina pagamento"
                  onClick={onOpenModal}
                />
              )}
            </DeleteInvoiceAction>
          ),
          (userIsAdmin || userIsFinance) && invoice.status !== InvoiceStatus.NOT_PAID && (
            <ManageInvoiceCommissionsAction
              invoice={invoice}
              onSuccess={onEdit}
            >
              {({ manageCommissions }) => (
                <Dropdown.Option
                  label="Gestisci commissioni"
                  aria-label="Gestisci commissioni"
                  onClick={manageCommissions}
                />
              )}
            </ManageInvoiceCommissionsAction>
          ),
        ] : undefined}
      >
        {(isAssignmentLoading || isPropertyLoading || isContactLoading || isCompanyLoading) && (
          <div style={{ width: '10rem' }}>
            <UserPreviewSkeleton size="S" />
          </div>
        )}
        {(assignmentError || propertyError || contactError || companyError) && (
          <Message type="critical" message="Impossibile caricare" />
        )}
        {context === 'HOLDER' && property && (
          <ViewPropertyPopoverPreview
            property={property}
            trigger={(
              <HStack gap={100} wrap="nowrap">
                <PropertyAvatar size="S" propertyType={property.propertyType} />
                <Text.BodySmall
                  fontWeight={FontWeight.MEDIUM}
                  css={setVerticalEllipsis(2)}
                >
                  {property.geo?.normalizedAddress}
                </Text.BodySmall>
              </HStack>
            )}
          />
        )}
        {context === 'PROPERTY' && contact && (
          <ViewContactPopoverPreview
            contact={contact}
            trigger={(
              <UserPreview
                name={contact.name || NO_VALUE_SYMBOL}
                size="S"
              >
                {contact.phoneNumber || NO_VALUE_SYMBOL}
              </UserPreview>
            )}
          />
        )}
        {context === 'PROPERTY' && company && (
          <ViewCompanyPopoverPreview
            company={company}
            trigger={(
              <HStack gap={100} wrap="nowrap">
                <CompanyAvatar size="S" />
                <Text.BodySmall
                  fontWeight={FontWeight.MEDIUM}
                  css={setVerticalEllipsis(2)}
                >
                  {company.name}
                </Text.BodySmall>
              </HStack>
            )}
          />
        )}
      </Card.Header>
      <Card.Content>
        <Stack gap={150}>
          <HStack gap={150} wrap="nowrap">
            <Badge
              label={invoiceTypeLabels[invoice.type]}
              color="neutral"
              size="XS"
            />
            <UpdateInvoiceStatus
              invoice={invoice}
              emphasis="low"
              size="XS"
              onSuccess={onEdit}
              canEditType={canEdit}
            />
          </HStack>
          <HStack gap={150} wrap="nowrap">
            <DetailStack label={amountLabel}>
              <InvoiceValuePopover
                invoice={invoice}
                trigger={(
                  <Text.Caption
                    fontWeight={FontWeight.REGULAR}
                    whitespace="nowrap"
                    lineHeight="1"
                  >
                    {formatEuro(displayAmount)}
                  </Text.Caption>
                )}
              />
            </DetailStack>
            <DetailStack label={paymentDateLabel}>
              <DateViewer stringDate={invoice.paymentDate} highlightWhen={invoice.status !== InvoiceStatus.COLLECTED ? getCriticalIfInThePast : undefined} />
            </DetailStack>
          </HStack>
        </Stack>
        {[InvoiceStatus.PROFORMA, InvoiceStatus.NOT_PAID].includes(invoice.status) && (
          <>
            <Divider />
            <DetailStack label="Rif. Proforma">
              {invoice.proformaInvoiceId}
            </DetailStack>
          </>
        )}
        {[InvoiceStatus.PAID, InvoiceStatus.COLLECTED].includes(invoice.status) && (
          <>
            <Divider />
            <DetailStack label="Rif. Fattura">
              {invoice.invoiceId}
            </DetailStack>
          </>
        )}
      </Card.Content>
    </Card>
  );
};

export default ViewInvoiceCard;
